if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelectorAll('.product-media-slider-dots .slider-counter__link'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Set up dots navigation
        if (this.elements.dots && this.elements.dots.length > 0) {
          this.elements.dots.forEach((dot, index) => {
            dot.addEventListener('click', (e) => {
              e.preventDefault();
              // Get all slides in order
              const slides = Array.from(this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]'));
              if (slides[index]) {
                const mediaId = slides[index].dataset.mediaId;
                this.setActiveMedia(mediaId, false);
                // Scroll to the slide
                if (this.elements.viewer.slider) {
                  slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
              }
            });
          });
        }

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        
        // Also listen to scroll events to update dots (fallback)
        if (this.elements.dots && this.elements.dots.length > 0) {
          const updateDotsFromScroll = debounce(() => {
            if (!this.elements.viewer.slider) return;
            const slides = Array.from(this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]'));
            if (slides.length === 0) return;
            
            // Find which slide is most visible
            let mostVisibleIndex = 0;
            let maxVisibility = 0;
            
            slides.forEach((slide, index) => {
              const rect = slide.getBoundingClientRect();
              const sliderRect = this.elements.viewer.slider.getBoundingClientRect();
              const visibleWidth = Math.min(rect.right, sliderRect.right) - Math.max(rect.left, sliderRect.left);
              const visibility = Math.max(0, visibleWidth) / rect.width;
              
              if (visibility > maxVisibility) {
                maxVisibility = visibility;
                mostVisibleIndex = index;
              }
            });
            
            // Update dots
            this.elements.dots.forEach((dot, index) => {
              if (index === mostVisibleIndex) {
                dot.classList.add('slider-counter__link--active');
                dot.setAttribute('aria-current', 'true');
              } else {
                dot.classList.remove('slider-counter__link--active');
                dot.removeAttribute('aria-current');
              }
            });
          }, 100);
          
          if (this.elements.viewer.slider) {
            this.elements.viewer.slider.addEventListener('scroll', updateDotsFromScroll);
          }
        }
        
        // Initialize active dot on page load
        if (this.elements.dots && this.elements.dots.length > 0) {
          setTimeout(() => {
            // Set first dot as active initially
            this.elements.dots[0]?.classList.add('slider-counter__link--active');
            this.elements.dots[0]?.setAttribute('aria-current', 'true');
          }, 100);
        }
        
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        
        // Update active dot when slide changes
        if (this.elements.dots && this.elements.dots.length > 0 && event.detail.currentPage) {
          // currentPage is 1-indexed, convert to 0-indexed
          const activeIndex = event.detail.currentPage - 1;
          
          // Ensure index is within bounds
          if (activeIndex >= 0 && activeIndex < this.elements.dots.length) {
            this.elements.dots.forEach((dot, index) => {
              if (index === activeIndex) {
                dot.classList.add('slider-counter__link--active');
                dot.setAttribute('aria-current', 'true');
              } else {
                dot.classList.remove('slider-counter__link--active');
                dot.removeAttribute('aria-current');
              }
            });
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // Update active dot - find index by matching slide order
        if (this.elements.dots && this.elements.dots.length > 0) {
          // Get slides in DOM order (matching slider order)
          const slides = Array.from(this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]'));
          const activeIndex = slides.indexOf(activeMedia);
          
          if (activeIndex >= 0 && activeIndex < this.elements.dots.length) {
            this.elements.dots.forEach((dot, index) => {
              if (index === activeIndex) {
                dot.classList.add('slider-counter__link--active');
                dot.setAttribute('aria-current', 'true');
              } else {
                dot.classList.remove('slider-counter__link--active');
                dot.removeAttribute('aria-current');
              }
            });
          }
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
